import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import {
  Loader2, Save, Plus, Trash2, ArrowLeft, Eye, Send, FileDown,
  Package, LayoutTemplate, Sparkles, Tag, Layers, CheckCircle2,
  Sliders, Printer, ShieldCheck, AlertCircle, Building, Copy
} from "lucide-react";
import api, { tokenStorage, API } from "../../api";

const TEMPLATE_STYLES = [
  {
    id: "swiss_minimal",
    name: "Swiss Minimalist",
    badge: "STANDARD",
    accent: "#E63946",
    desc: "Klassisches Schweizer High-End Design mit präziser Typografie und roten Akzenten.",
  },
  {
    id: "modern_dark",
    name: "RedWORK Cyber Dark",
    badge: "MODERN",
    accent: "#ef4444",
    desc: "Edler Tech-Look mit dunklem Header und fokussierten Kontrasten für Cloud & IT.",
  },
  {
    id: "corporate_blue",
    name: "Swiss Corporate Navy",
    badge: "BUSINESS",
    accent: "#0369a1",
    desc: "Konservatives Banken- und Treuhand-Layout in edlem Saphirblau.",
  },
  {
    id: "classic_clean",
    name: "Classic Editorial",
    badge: "CLEAN",
    accent: "#475569",
    desc: "Zurückhaltend und elegant mit dezenter Linienführung und maximalem Weissraum.",
  },
];

const INVOICE_CATEGORIES = [
  "Allgemein",
  "Webhosting & Domain",
  "Cloud Server & VPS",
  "Webdesign & UX",
  "Software-Entwicklung",
  "SEO & Marketing",
  "Wartung & SLA Support",
  "Consulting & Beratung",
];

const EXTRA_ADDON_PRESETS = [
  { category: "Webhosting & Domain", description: "Dedizierte Schweizer IPv4-Adresse (Zürich)", unitPrice: 35.0, isExtra: true, details: "Inkl. rDNS & DDoS-Schutz" },
  { category: "Webhosting & Domain", description: "Tägliches Offsite-Backup & 30-Tage Snapshot Archiv", unitPrice: 20.0, isExtra: true, details: "Georedundant in CH-Rechenzentrum" },
  { category: "Webhosting & Domain", description: "Managed SSL Wildcard Zertifikat (256-Bit ECC)", unitPrice: 80.0, isExtra: true, details: "Automatisches Auto-Renewal" },
  { category: "Cloud Server & VPS", description: "Upgrade: +8 GB High-Speed ECC RAM", unitPrice: 40.0, isExtra: true, details: "Hot-add ohne Downtime" },
  { category: "Cloud Server & VPS", description: "Upgrade: +100 GB NVMe Gen4 Storage Pool", unitPrice: 30.0, isExtra: true, details: "Über 700'000 IOPS Performance" },
  { category: "Wartung & SLA Support", description: "Priority 24/7 SLA Notfallbereitschaft (< 15 Min.)", unitPrice: 120.0, isExtra: true, details: "Direkter Notfallkanal Zürich" },
  { category: "SEO & Marketing", description: "Monatlicher SEO Audit & Google Search Console Report", unitPrice: 150.0, isExtra: true, details: "Detaillierte Ranking- und Keyword-Analyse" },
];

const EMPTY = {
  companyId: "", title: "", intro: "", notes: "",
  clientName: "", clientStreet: "", clientZip: "", clientCity: "", clientCountry: "CH", clientEmail: "",
  issueDate: new Date().toISOString().slice(0, 10), dueDate: "",
  items: [], vatRate: null, currency: "CHF", reference: "", status: "draft",
  templateStyle: "swiss_minimal", category: "Allgemein",
  recurring: false, recurringInterval: "monthly", recurringNextDate: "", recurringEndDate: "",
};

const inp = "w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] focus:outline-none text-sm bg-white text-[#0f172a] transition";

export default function InvoiceEditor({ mode = "invoice" }) {
  const isOffer = mode === "offer";
  const path = isOffer ? "offers" : "invoices";
  const label = isOffer ? "Offerte" : "Rechnung";

  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();

  const [doc, setDoc] = useState(EMPTY);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [addonPickerOpen, setAddonPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [co, pr, ca, tp] = await Promise.all([
        api.get("/admin/companies"),
        api.get("/admin/products"),
        api.get("/admin/product-categories"),
        api.get("/admin/invoice-templates"),
      ]);
      setCompanies(co.data); setProducts(pr.data); setCats(ca.data); setTemplates(tp.data);

      if (id) {
        const r = await api.get(`/admin/${path}/${id}`);
        setDoc({ ...EMPTY, ...r.data });
      } else {
        const def = co.data.find((c) => c.isDefault) || co.data[0];
        if (def) {
          const isVatOn = def.vatEnabled !== false;
          setDoc((d) => ({
            ...d,
            companyId: def.id,
            vatRate: isVatOn ? (def.defaultVatRate ?? 8.1) : 0,
            currency: def.currency || "CHF",
          }));
        }
        // Auto-generate PO / Reference number if new document
        const yr = new Date().getFullYear();
        const rand = Math.floor(1000 + Math.random() * 9000);
        const tag = isOffer ? "REF" : "PO";
        setDoc((d) => ({
          ...d,
          reference: d.reference || `${tag}-${yr}-KND-${rand}`,
        }));
        const tplId = location.state?.templateId;
        if (tplId) {
          const tpl = tp.data.find((t) => t.id === tplId);
          if (tpl) {
            setDoc((d) => ({
              ...d,
              title: tpl.title || "",
              intro: tpl.intro || "",
              notes: tpl.notes || "",
              items: (tpl.items || []).map((it) => ({ ...it })),
            }));
          }
        }
      }
    } finally { setLoading(false); }
  }, [id, path, location.state]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const subtotal = doc.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0);
    const vat = subtotal * ((Number(doc.vatRate) || 0) / 100);
    return { subtotal: subtotal.toFixed(2), vat: vat.toFixed(2), total: (subtotal + vat).toFixed(2) };
  }, [doc.items, doc.vatRate]);

  const addItem = (preset = {}) => setDoc((d) => ({
    ...d,
    items: [...d.items, {
      description: "",
      quantity: 1,
      unitPrice: 0,
      category: doc.category || "Allgemein",
      isExtra: false,
      details: "",
      ...preset,
    }],
  }));

  const updItem = (idx, patch) => setDoc((d) => ({
    ...d,
    items: d.items.map((it, i) => i === idx ? { ...it, ...patch } : it),
  }));

  const remItem = (idx) => setDoc((d) => ({
    ...d,
    items: d.items.filter((_, i) => i !== idx),
  }));

  const addFromProduct = (p) => addItem({
    productId: p.id,
    description: p.name + (p.description ? `\n${p.description}` : ""),
    unitPrice: Number(p.unitPrice),
    quantity: 1,
    category: p.categoryName || doc.category || "Allgemein",
  });

  const addFromAddon = (addon) => {
    addItem({
      description: addon.description,
      unitPrice: addon.unitPrice,
      quantity: 1,
      category: addon.category,
      isExtra: true,
      details: addon.details,
    });
    setAddonPickerOpen(false);
  };

  const save = async () => {
    if (!doc.clientName.trim()) { setFeedback({ type: "err", text: "Bitte Kundenname eingeben" }); return; }
    if (doc.items.length === 0) { setFeedback({ type: "err", text: "Bitte mindestens eine Position hinzufügen" }); return; }
    setSaving(true); setFeedback(null);
    try {
      const payload = { ...doc };
      payload.items = doc.items.map((it) => ({
        description: it.description,
        quantity: Number(it.quantity) || 0,
        unitPrice: Number(it.unitPrice) || 0,
        productId: it.productId || "",
        category: it.category || "",
        isExtra: !!it.isExtra,
        details: it.details || "",
      }));
      payload.vatRate = doc.vatRate === null || doc.vatRate === "" ? null : Number(doc.vatRate);
      delete payload.id; delete payload.createdAt; delete payload.number;
      delete payload.subtotal; delete payload.vatAmount; delete payload.total;
      delete payload.sentAt; delete payload.paidAt; delete payload.type;

      let saved;
      if (id) {
        const r = await api.put(`/admin/${path}/${id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post(`/admin/${path}`, payload);
        saved = r.data;
        nav(`/admin/${path}/${saved.id}`, { replace: true });
      }
      setDoc({ ...EMPTY, ...saved });
      setFeedback({ type: "ok", text: `${label} erfolgreich mit Schweizer QR-Bill gespeichert (${saved.number})` });
    } catch (e) {
      setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler beim Speichern" });
    } finally { setSaving(false); }
  };

  const previewWithToken = async (action) => {
    const token = tokenStorage.get();
    const url = `${API}/admin/${path}/${id}/${action}`;
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (action === "preview") {
        const html = await r.text();
        const w = window.open("", "_blank");
        if (w) { w.document.open(); w.document.write(html); w.document.close(); }
      } else {
        if (!r.ok) {
          let msg = `HTTP ${r.status}`; try { msg = (await r.json()).detail || msg; } catch (e) { /* ignore */ }
          throw new Error(msg);
        }
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${label}-${doc.number || id}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      }
    } catch (e) { setFeedback({ type: "err", text: e.message || "Fehler" }); }
  };

  const sendDoc = async () => {
    if (!doc.clientEmail) { setFeedback({ type: "err", text: "Bitte erst Kunden-E-Mail eintragen und speichern." }); return; }
    if (!window.confirm(`${label} an ${doc.clientEmail} senden?`)) return;
    setSaving(true);
    try {
      const r = await api.post(`/admin/${path}/${id}/send`, { toEmail: doc.clientEmail });
      if (r.data.ok) {
        setFeedback({ type: "ok", text: `Gesendet an ${r.data.to}` });
        setDoc((d) => ({ ...d, status: "sent" }));
      } else setFeedback({ type: "err", text: `Fehler: ${r.data.error}` });
    } catch (e) { setFeedback({ type: "err", text: e.response?.data?.detail || "Fehler" }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto text-[#E63946]" /></div>;

  if (companies.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-card p-8 text-center max-w-xl mx-auto">
        <h2 className="text-xl font-bold text-[#0f172a]">Bitte zuerst eine Firma anlegen</h2>
        <p className="text-[#64748b] mt-2">Du brauchst mindestens eine Schweizer Firma mit IBAN, um QR-Rechnungen zu erstellen.</p>
        <Link to="/admin/companies" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg bg-[#E63946] text-white font-bold text-sm">Zur Firmenverwaltung</Link>
      </div>
    );
  }

  return (
    <div data-testid="invoice-editor" className="space-y-6">
      {/* Top Action Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Link to={`/admin/${path}`} className="w-9 h-9 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-600 transition">
            <ArrowLeft size={16} />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-black text-[#0f172a]">{id ? `${label} bearbeiten` : `Neue ${label}`}</h1>
              {doc.number && <span className="text-xs font-mono font-bold px-2 py-0.5 bg-red-50 text-red-600 border border-red-200 rounded-md">#{doc.number}</span>}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Schweizer QR-Bill Standard • SIX Payment Services Konform</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {id && (
            <>
              <button onClick={() => previewWithToken("preview")} className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs inline-flex items-center gap-1.5 shadow-sm transition">
                <Eye size={14} className="text-blue-600" /> HTML Vorschau & Druck
              </button>
              <button onClick={() => previewWithToken("pdf")} className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-black text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-sm transition">
                <FileDown size={14} className="text-red-400" /> Swiss QR-PDF
              </button>
            </>
          )}
          <button onClick={save} disabled={saving} className="px-5 py-2 rounded-xl bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 text-white font-bold text-xs inline-flex items-center gap-2 shadow-sm transition">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Speichern
          </button>
        </div>
      </div>

      {feedback && (
        <div className={`p-4 rounded-xl text-sm font-semibold flex items-center gap-2 ${feedback.type === "ok" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-red-50 border border-red-200 text-red-800"}`}>
          {feedback.type === "ok" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{feedback.text}</span>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left Column: Form Details */}
        <div className="lg:col-span-8 space-y-6">
          {/* Style Template Selector Showcase */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <LayoutTemplate className="h-4 w-4 text-[#E63946]" />
                <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">1. Schweizer Rechnungs-Vorlage wählen</h3>
              </div>
              <span className="text-[11px] font-bold text-slate-400">4 Premium Vorlagen</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TEMPLATE_STYLES.map((tpl) => {
                const isSelected = (doc.templateStyle || "swiss_minimal") === tpl.id;
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setDoc({ ...doc, templateStyle: tpl.id })}
                    className={`p-3.5 rounded-xl border text-left transition-all relative overflow-hidden ${isSelected ? "border-[#E63946] bg-red-50/20 ring-1 ring-[#E63946] shadow-sm" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: tpl.accent }} />
                        <span className="text-xs font-bold text-[#0f172a]">{tpl.name}</span>
                      </div>
                      <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${isSelected ? "bg-[#E63946] text-white" : "bg-slate-100 text-slate-600"}`}>
                        {tpl.badge}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">{tpl.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Category & Document Basics */}
          <Card title="Dokument-Stammdaten & Kategorie" icon={Layers}>
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Kategorie / Geschäftsbereich">
                <select
                  value={doc.category || "Allgemein"}
                  onChange={(e) => setDoc({ ...doc, category: e.target.value })}
                  className={inp}
                >
                  {INVOICE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </Field>

              <Field label="Ausstellende Schweizer Firma">
                <select
                  value={doc.companyId || ""}
                  onChange={(e) => {
                    const cid = e.target.value;
                    const comp = companies.find((c) => c.id === cid);
                    if (comp) {
                      const isVatOn = comp.vatEnabled !== false;
                      setDoc((d) => ({
                        ...d,
                        companyId: cid,
                        vatRate: isVatOn ? (comp.defaultVatRate ?? 8.1) : 0,
                        currency: comp.currency || d.currency || "CHF",
                      }));
                    } else {
                      setDoc((d) => ({ ...d, companyId: cid }));
                    }
                  }}
                  className={inp}
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.isDefault ? " (Hauptfirma)" : ""}{c.vatEnabled === false ? " — Ohne MwSt (0%)" : ` — MwSt ${c.defaultVatRate ?? 8.1}%`}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Dokument-Titel">
                <input
                  value={doc.title || ""}
                  onChange={(e) => setDoc({ ...doc, title: e.target.value })}
                  placeholder={`${label} für Cloud Services`}
                  className={inp}
                />
              </Field>

              <Field label="Rechnungsdatum">
                <input
                  type="date"
                  value={doc.issueDate || ""}
                  onChange={(e) => setDoc({ ...doc, issueDate: e.target.value })}
                  className={inp}
                />
              </Field>

              {!isOffer && (
                <Field label="Fällig bis (Zahlungsziel)">
                  <input
                    type="date"
                    value={doc.dueDate || ""}
                    onChange={(e) => setDoc({ ...doc, dueDate: e.target.value })}
                    className={inp}
                  />
                </Field>
              )}

              <Field label="Referenz / PO-Nummer">
                <input
                  value={doc.reference || ""}
                  onChange={(e) => setDoc({ ...doc, reference: e.target.value })}
                  placeholder="z.B. PO-98421"
                  className={inp}
                />
              </Field>
            </div>
          </Card>

          {/* Client Block */}
          <Card title="Rechnungsempfänger / Kunde" icon={Building}>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Kundenname / Firmenname *">
                <input
                  value={doc.clientName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDoc((prev) => {
                      const yr = new Date().getFullYear();
                      const rand = Math.floor(1000 + Math.random() * 9000);
                      const tag = isOffer ? "REF" : "PO";
                      const cleanPrefix = (val || "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase() || "KND";
                      // Auto-update reference if empty or previously auto-generated
                      const isAutoRef = !prev.reference || prev.reference.startsWith("PO-") || prev.reference.startsWith("REF-");
                      return {
                        ...prev,
                        clientName: val,
                        reference: isAutoRef ? `${tag}-${yr}-${cleanPrefix}-${rand}` : prev.reference,
                      };
                    });
                  }}
                  placeholder="Muster Solutions AG"
                  className={inp}
                />
              </Field>
              <Field label="Kunden E-Mail">
                <input
                  type="email"
                  value={doc.clientEmail || ""}
                  onChange={(e) => setDoc({ ...doc, clientEmail: e.target.value })}
                  placeholder="buchhaltung@muster.ch"
                  className={inp}
                />
              </Field>
              <Field label="Strasse & Hausnummer">
                <input
                  value={doc.clientStreet || ""}
                  onChange={(e) => setDoc({ ...doc, clientStreet: e.target.value })}
                  placeholder="Bahnhofstrasse 10"
                  className={inp}
                />
              </Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="PLZ">
                  <input
                    value={doc.clientZip || ""}
                    onChange={(e) => setDoc({ ...doc, clientZip: e.target.value })}
                    placeholder="8001"
                    className={inp}
                  />
                </Field>
                <div className="col-span-2">
                  <Field label="Ort">
                    <input
                      value={doc.clientCity || ""}
                      onChange={(e) => setDoc({ ...doc, clientCity: e.target.value })}
                      placeholder="Zürich"
                      className={inp}
                    />
                  </Field>
                </div>
              </div>
            </div>
          </Card>

          {/* Positionen & Extra Add-on Bar */}
          <Card
            title="Positionen, Leistungen & Zusatzoptionen"
            icon={Package}
            right={(
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setAddonPickerOpen(true)}
                  className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-[#E63946] border border-red-200 text-xs font-bold inline-flex items-center gap-1.5 transition"
                >
                  <Sparkles size={13} /> + Zusatzoption
                </button>
                <button
                  type="button"
                  onClick={() => setProductPickerOpen(true)}
                  className="px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-[#1E88E5] border border-blue-200 text-xs font-bold inline-flex items-center gap-1.5 transition"
                >
                  <Package size={13} /> Aus Katalog
                </button>
                <button
                  type="button"
                  onClick={() => addItem()}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold inline-flex items-center gap-1.5 transition"
                >
                  <Plus size={13} /> Manuell
                </button>
              </div>
            )}
          >
            {doc.items.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                <Package className="h-8 w-8 text-slate-400 mx-auto mb-2 opacity-50" />
                <p className="text-xs text-slate-500 font-medium">Noch keine Positionen hinzugefügt.</p>
                <p className="text-[11px] text-slate-400 mt-1">Nutze die Buttons oben für Katalogprodukte oder Schweizer Zusatzoptionen.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {doc.items.map((it, idx) => (
                  <div key={idx} className={`p-3.5 rounded-xl border transition-all ${it.isExtra ? "bg-amber-50/30 border-amber-200/80" : "bg-slate-50/70 border-slate-200/80"}`}>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <div className="col-span-12 sm:col-span-6 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-mono font-bold text-slate-400">#{idx + 1}</span>
                          {it.isExtra && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-red-100 text-red-700 uppercase tracking-wider">
                              ZUSATZOPTION
                            </span>
                          )}
                          <input
                            type="text"
                            value={it.category || ""}
                            onChange={(e) => {
                              const newCat = e.target.value;
                              updItem(idx, { category: newCat });
                            }}
                            placeholder="Kategorie (z.B. Webhosting)"
                            className="text-[10px] px-2 py-0.5 rounded bg-white border border-slate-200 text-slate-600 font-semibold"
                          />

                          {/* Automatisches Abrechnungsintervall für Webhosting & Server */}
                          {( (it.category || "").toLowerCase().includes("hosting") || (doc.category || "").toLowerCase().includes("hosting") ) && (
                            <div className="inline-flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200 ml-auto">
                              <button
                                type="button"
                                onClick={() => {
                                  const currentPrice = Number(it.unitPrice) || 0;
                                  const base = it.billingCycle === "yearly" ? currentPrice / 12 : currentPrice;
                                  updItem(idx, {
                                    billingCycle: "monthly",
                                    unitPrice: Number(base.toFixed(2)),
                                    details: "Abrechnung: Monatlich"
                                  });
                                }}
                                className={`px-2 py-0.5 rounded text-[9px] font-black transition ${it.billingCycle !== "yearly" ? "bg-white text-[#E63946] shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
                              >
                                Monatlich
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const currentPrice = Number(it.unitPrice) || 0;
                                  const base = it.billingCycle !== "yearly" ? currentPrice : currentPrice / 12;
                                  // 12 months with 10% Swiss prepayment discount
                                  const yearlyPrice = Number((base * 12 * 0.9).toFixed(2));
                                  updItem(idx, {
                                    billingCycle: "yearly",
                                    unitPrice: yearlyPrice,
                                    details: "Abrechnung: Jährlich (-10% Rabatt inkl.)"
                                  });
                                }}
                                className={`px-2 py-0.5 rounded text-[9px] font-black transition ${it.billingCycle === "yearly" ? "bg-[#E63946] text-white shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
                              >
                                Jährlich (-10%)
                              </button>
                            </div>
                          )}
                        </div>
                        <textarea
                          value={it.description}
                          onChange={(e) => updItem(idx, { description: e.target.value })}
                          placeholder="Positionsbezeichnung & Leistungsbeschreibung"
                          rows={2}
                          className={inp}
                        />
                        <input
                          type="text"
                          value={it.details || ""}
                          onChange={(e) => updItem(idx, { details: e.target.value })}
                          placeholder="Zusätzliche technische Details (optional)"
                          className="w-full px-2.5 py-1 rounded-lg border border-slate-200 text-[11px] bg-white text-slate-600"
                        />
                      </div>

                      <div className="col-span-4 sm:col-span-2">
                        <label className="text-[10px] font-bold text-slate-400 block mb-1">Menge</label>
                        <input
                          type="number"
                          step="0.5"
                          value={it.quantity}
                          onChange={(e) => updItem(idx, { quantity: e.target.value })}
                          className={inp}
                        />
                      </div>

                      <div className="col-span-4 sm:col-span-2">
                        <label className="text-[10px] font-bold text-slate-400 block mb-1">Einzelpreis</label>
                        <input
                          type="number"
                          step="0.01"
                          value={it.unitPrice}
                          onChange={(e) => updItem(idx, { unitPrice: e.target.value })}
                          className={inp}
                        />
                      </div>

                      <div className="col-span-3 sm:col-span-1 text-right pt-6">
                        <span className="text-xs font-mono font-black text-slate-900 block">
                          {((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0)).toFixed(2)}
                        </span>
                      </div>

                      <div className="col-span-1 pt-5 flex justify-end">
                        <button
                          type="button"
                          onClick={() => remItem(idx)}
                          className="w-8 h-8 rounded-lg bg-white hover:bg-red-50 text-slate-400 hover:text-red-600 border border-slate-200 flex items-center justify-center transition"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Notes & Terms */}
          <Card title="Hinweise & Zahlungsbedingungen" icon={ShieldCheck}>
            <textarea
              value={doc.notes || ""}
              onChange={(e) => setDoc({ ...doc, notes: e.target.value })}
              rows={3}
              placeholder="Individuelle Notizen, SLA Vereinbarungen oder Projektreferenzen..."
              className={inp}
            />
          </Card>
        </div>

        {/* Right Column: Swiss QR Summary & Quick Actions */}
        <div className="lg:col-span-4 space-y-5">
          {/* Summary Box */}
          <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm sticky top-4 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">Gesamttotal & MwSt</h3>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                100% CHF
              </span>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-slate-600">
                <span>Zwischensumme:</span>
                <span className="font-mono font-semibold">{doc.currency || "CHF"} {totals.subtotal}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>{companies.find((c) => c.id === doc.companyId)?.vatEnabled === false || Number(doc.vatRate) === 0 ? "MwSt (Befreit / 0%):" : `MwSt (${doc.vatRate ?? 8.1}%):`}</span>
                <span className="font-mono font-semibold">{doc.currency || "CHF"} {totals.vat}</span>
              </div>
              <div className="pt-2 border-t border-slate-200 flex justify-between items-center">
                <span className="font-extrabold text-base text-slate-900">Total Betrag:</span>
                <span className="font-mono text-lg font-black text-[#E63946]">{doc.currency || "CHF"} {totals.total}</span>
              </div>
            </div>

            {/* Swiss QR Bill Verification Badge */}
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1.5">
              <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                <span>Swiss QR-Bill Status: Bereit</span>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Rechnung wird automatisch mit offiziellem Schweizer QR-Code gemäss SIX Payment Services generiert.
              </p>
            </div>

            {/* Primary Action Buttons */}
            <div className="space-y-2 pt-2">
              <button
                onClick={save}
                disabled={saving}
                className="w-full py-2.5 rounded-xl bg-[#E63946] hover:bg-[#c5303d] disabled:opacity-50 text-white font-bold text-xs inline-flex items-center justify-center gap-2 shadow-sm transition"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Dokument Speichern
              </button>

              {id && (
                <>
                  <button
                    onClick={() => previewWithToken("preview")}
                    className="w-full py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs inline-flex items-center justify-center gap-2 transition"
                  >
                    <Eye size={14} className="text-blue-600" /> Vorschau & Drucken
                  </button>
                  <button
                    onClick={() => previewWithToken("pdf")}
                    className="w-full py-2 rounded-xl bg-slate-900 hover:bg-black text-white font-bold text-xs inline-flex items-center justify-center gap-2 transition"
                  >
                    <FileDown size={14} className="text-red-400" /> PDF mit QR-Bill herunterladen
                  </button>
                  <button
                    onClick={sendDoc}
                    disabled={!doc.clientEmail}
                    className="w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-bold text-xs inline-flex items-center justify-center gap-2 transition"
                  >
                    <Send size={14} /> An Kunde per E-Mail senden
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Swiss Extra Add-on Modal */}
      {addonPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAddonPickerOpen(false)}>
          <div className="bg-white rounded-2xl max-w-xl w-full p-5 space-y-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#E63946]" />
                <h3 className="text-base font-extrabold text-[#0f172a]">Schweizer Zusatzoptionen (Add-ons)</h3>
              </div>
              <button onClick={() => setAddonPickerOpen(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {EXTRA_ADDON_PRESETS.map((addon, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => addFromAddon(addon)}
                  className="w-full text-left p-3 rounded-xl border border-slate-200 hover:border-red-400 hover:bg-red-50/20 transition flex items-center justify-between gap-3 group"
                >
                  <div>
                    <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">{addon.category}</span>
                    <p className="text-xs font-bold text-slate-800 group-hover:text-red-600 transition-colors">{addon.description}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{addon.details}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-xs font-mono font-bold text-slate-900">+CHF {addon.unitPrice.toFixed(2)}</span>
                    <span className="block text-[9px] font-bold text-emerald-600">Hinzufügen →</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Catalog Product Picker Modal */}
      {productPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setProductPickerOpen(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-5 space-y-4 shadow-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-blue-600" />
                <h3 className="text-base font-extrabold text-[#0f172a]">Produkt aus Katalog wählen</h3>
              </div>
              <button onClick={() => setProductPickerOpen(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            <div className="overflow-y-auto space-y-3 flex-1 pr-1">
              {cats.map((c) => {
                const list = products.filter((p) => p.categoryId === c.id);
                if (list.length === 0) return null;
                return (
                  <div key={c.id}>
                    <h4 className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">{c.name}</h4>
                    <div className="space-y-1.5">
                      {list.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => { addFromProduct(p); setProductPickerOpen(false); }}
                          className="w-full flex items-center justify-between gap-4 p-2.5 rounded-xl border border-slate-100 hover:border-blue-400 hover:bg-blue-50/20 text-left transition"
                        >
                          <div>
                            <div className="text-xs font-bold text-slate-800">{p.name}</div>
                            <div className="text-[11px] text-slate-500 truncate max-w-md">{p.description}</div>
                          </div>
                          <div className="text-xs font-mono font-bold text-slate-900 whitespace-nowrap">
                            CHF {Number(p.unitPrice).toFixed(2)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, icon: Icon, right, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-[#E63946]" />}
          <h3 className="text-sm font-black uppercase tracking-wider text-slate-800">{title}</h3>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}
